<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
    </div>

    <div class="page-container">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchPlatforms">重試</button>
    </div>

    <div v-else class="platforms-container">
      <div class="platforms-header">
        <div class="header-actions">
          <p class="text-muted">選擇要新增的平台類型：</p>
          <div class="platform-options">
            <button
              v-for="platform in availablePlatforms"
              :key="platform.code"
              class="btn platform-btn"
              :class="configuredCodes.has(platform.code) ? 'btn-configured' : 'btn-primary'"
              :disabled="configuredCodes.has(platform.code)"
              :title="configuredCodes.has(platform.code) ? `${platform.name} 已設定，請至下方編輯` : `新增 ${platform.name} 設定`"
              @click="handleCreateConfig(platform.code)"
            >
              <i :class="platform.icon"></i>
              {{ platform.name }}
              <span v-if="configuredCodes.has(platform.code)" class="configured-tag">已設定</span>
            </button>
          </div>
          <div style="margin-top: 16px; padding-top: 16px; border-top: 1px solid #e0e0e0;">
            <p class="text-muted" style="margin-bottom: 8px;">POS 系統整合：</p>
            <button
              class="btn btn-primary platform-btn"
              @click="handleGoToWixtar"
            >
              <i class="ri-terminal-box-line"></i>
              Wixtar 全通路整合
            </button>
            <small class="form-text" style="display: block; margin-top: 8px; color: #6c757d;">
              Wixtar 全通路系統使用獨立的整合設定頁面
            </small>
          </div>
        </div>
      </div>

      <div v-if="platforms.length === 0" class="empty-state">
        <p>尚未設定任何電商平台</p>
        <p class="text-muted">請從上方選擇要設定的平台類型</p>
      </div>

      <div v-else class="platforms-list">
        <div
          v-for="platform in platforms"
          :key="platform.id"
          class="platform-card"
        >
          <div class="platform-header">
            <div class="platform-info">
              <h3>{{ platform.platformName || getPlatformName(platform.platform) }}</h3>
              <div class="platform-badges">
                <span class="badge badge-info">{{ getPlatformName(platform.platform) }}</span>
                <span
                  :class="platform.isActive ? 'badge badge-success' : 'badge badge-secondary'"
                >
                  {{ platform.isActive ? '已啟用' : '未啟用' }}
              </span>
              </div>
            </div>
            <div class="platform-actions">
              <button
                class="btn btn-sm btn-secondary"
                @click="handleEditConfig(platform)"
              >
                <i class="ri-edit-line"></i>
                編輯
              </button>
              <button
                class="btn btn-sm btn-danger"
                @click="handleDeleteConfig(platform)"
                :disabled="deleting === platform.id"
              >
                <i class="ri-delete-bin-line"></i>
                {{ deleting === platform.id ? '刪除中...' : '刪除' }}
              </button>
            </div>
          </div>

          <div class="platform-details">
            <div class="detail-item">
              <span class="detail-label">連線方式：</span>
              <span class="detail-value">{{ getConnectionTypeName(platform.connectionType) }}</span>
            </div>
            <div v-if="platform.lastSyncAt" class="detail-item">
              <span class="detail-label">最後同步：</span>
              <span class="detail-value">{{ formatDate(platform.lastSyncAt) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { ecomService } from '@/api/modules/ecom.js';
import { showError, showSuccess, showDeleteConfirm } from '@/utils/swal.js';

export default {
  name: 'EcomConfig',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('電商平台設定');

    const loading = ref(false);
    const error = ref(null);
    const platforms = ref([]);
    const deleting = ref(null);

    // 已設定過的平台代碼 set，用於 disable 新增按鈕
    const configuredCodes = computed(() => new Set(platforms.value.map(p => p.platform)));

    const availablePlatforms = [
      { code: 'shopify', name: 'Shopify', icon: 'ri-store-line' },
      { code: 'woocommerce', name: 'WooCommerce', icon: 'ri-shopping-cart-line' },
      { code: 'shopline', name: 'Shopline', icon: 'ri-shopping-bag-line' },
      { code: '91app', name: '91APP', icon: 'ri-shopping-basket-line' },
      { code: 'cyberbiz', name: 'CYBERBIZ', icon: 'ri-global-line' },
      { code: 'shopee', name: '蝦皮 Shopee', icon: 'ri-ship-line' },
      { code: 'meepshop', name: 'meepShop', icon: 'ri-layout-masonry-line' },
      { code: 'others', name: '其他', icon: 'ri-more-line' }
    ];

    const fetchPlatforms = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await ecomService.listPlatforms();
        if (response && response.success && response.data) {
          platforms.value = response.data.platforms || [];
        } else {
          error.value = response?.message || '獲取平台列表失敗';
        }
      } catch (err) {
        console.error('獲取平台列表失敗:', err);
        error.value = err.message || '獲取平台列表失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const getPlatformName = (platformCode) => {
      const platform = availablePlatforms.find(p => p.code === platformCode);
      return platform ? platform.name : platformCode;
    };

    const getConnectionTypeName = (type) => {
      const types = {
        webhook: 'Webhook',
        api_polling: 'API 輪詢',
        file_import: '檔案匯入'
      };
      return types[type] || type;
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW');
    };

    const handleCreateConfig = (platformCode) => {
      router.push({
        name: 'ClientEcomConfigCreate',
        params: { platform: platformCode }
      });
    };

    const handleEditConfig = (config) => {
      router.push({
        name: 'ClientEcomConfigEdit',
        params: { configId: config.id }
      });
    };

    const handleGoToWixtar = () => {
      router.push({ name: 'ClientWixtarConfig' });
    };

    const handleDeleteConfig = async (config) => {
      const platformName = config.platformName || getPlatformName(config.platform);
      const confirmed = await showDeleteConfirm(
        platformName,
        `確定要刪除「${platformName}」的設定嗎？此操作無法復原。`
      );

      if (!confirmed) {
        return;
      }

      deleting.value = config.id;
      try {
        const response = await ecomService.deletePlatformConfig(config.id);
        if (response && response.success) {
          await showSuccess('刪除成功', `${platformName} 設定已刪除`);
          await fetchPlatforms(); // 重新載入列表
        } else {
          await showError('刪除失敗', response?.message || '刪除設定失敗');
        }
      } catch (err) {
        console.error('刪除設定失敗:', err);
        await showError('刪除失敗', err.message || '刪除設定失敗');
      } finally {
        deleting.value = null;
      }
    };

    onMounted(() => {
      fetchPlatforms();
    });

    return {
      pageTitle,
      loading,
      error,
      platforms,
      availablePlatforms,
      configuredCodes,
      fetchPlatforms,
      getPlatformName,
      getConnectionTypeName,
      formatDate,
      handleCreateConfig,
      handleEditConfig,
      handleDeleteConfig,
      handleGoToWixtar,
      deleting
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.loading-state,
.error-state {
  text-align: center;
  padding: 40px;
}

.platforms-header {
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
}

.platforms-header .header-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.platforms-header .header-actions p {
  margin: 0;
  font-size: 14px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-state p {
  margin-bottom: 20px;
}

.text-muted {
  color: #6c757d;
  font-size: 14px;
}

.platform-options {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-top: 20px;
}

.platform-btn {
  min-width: 150px;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.btn-configured {
  background-color: #e9ecef;
  color: #6c757d;
  border: 1px solid #ced4da;
  cursor: not-allowed;
  opacity: 0.75;
}

.configured-tag {
  font-size: 11px;
  background: #6c757d;
  color: #fff;
  border-radius: 3px;
  padding: 1px 5px;
  margin-left: 2px;
}

.platforms-list {
  display: grid;
  gap: 20px;
}

.platform-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.platform-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.platform-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.platform-info h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.platform-actions {
  display: flex;
  gap: 8px;
}

.platform-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 12px;
  color: #6c757d;
}

.detail-value {
  font-size: 14px;
  font-weight: 500;
}

.badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.badge-success {
  background-color: #d4edda;
  color: #155724;
}

.badge-secondary {
  background-color: #e2e3e5;
  color: #383d41;
}

.badge-info {
  background-color: #d1ecf1;
  color: #0c5460;
  margin-right: 8px;
}

.platform-badges {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn-primary {
  background-color: #007bff;
  color: #fff;
}

.btn-primary:hover {
  background-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  color: #fff;
}

.btn-secondary:hover {
  background-color: #545b62;
}

.btn-danger {
  background-color: #dc3545;
  color: #fff;
}

.btn-danger:hover:not(:disabled) {
  background-color: #c82333;
}

.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  color: #dc3545;
  margin-bottom: 16px;
}
</style>
